import {
    Stack, TextField, IStackProps, Toggle,
    Label,
    PrimaryButton,
    Dropdown, IDropdownOption,
    DefaultButton,
} from "@fluentui/react";
import { OnlineWorkflowTemplates } from "src/types/workflows";
import React, { useEffect, useState } from "react";
import {
    createWorkflow,
    queryWorkflowTemplates,
    querySubstyles,
    queryStyles,
    queryLanguages,
    queryRegions,
    queryImageGenerationModels,
} from "src/services/aigc";
import { useNavigate } from "react-router-dom";
import style from "./index.module.scss";

const columnProps: Partial<IStackProps> = {
    tokens: { childrenGap: 15 },
    styles: { root: { width: 300 } },
};

const inputExist = (value: string, errMsg: string) => (
    value === "" || value === undefined || value === null ? errMsg : ""
);

const useVerification = (value: string, errMsg: string): [string | undefined, () => boolean] => {
    const [error, setError] = useState<string>();
    const verify = () => {
        setError(inputExist(value, errMsg));
        return inputExist(value, errMsg) === "";
    };
    return [
        error,
        verify,
    ];
};

export const WorkflowAdd: React.FC = () => {
    const [form, setForm] = useState({
        batch: "testing",
        topic: "",
        style: "",
        region: "",
        substyle: "",
        language: "",
        imageManualReviewOptions: {
            isEnabled: false,
        },
        outlineGenerationOptions: {
            useMockData: false,
            outlineType: "RichContent",
            rewrite: true,
            useCustomOutline: false,
            customOutline: null,
        },
        imageGenerationOptions: {
            useMockData: false,
            mjProxyType: 1,
            maxRetries: 10,
            retryIntervalSeconds: 30,
            mjImagineMaxRetries: 2,
            mjImagineTimeoutSeconds: 600,
            mjGenerationMode: "RELAX",
            imageGenerationModel: "",
        },
        themeGenerationOptions: {
            useMockData: false,
        },
    });
    const [workflowTemplateName, setWorkflowTemplateName] = useState("OnlineModern");
    const [templateStyle, setTemplateStyle] = useState("Modern");

    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<IDropdownOption[]>([]);
    const [
        workflowTemplateNameErr, verifyWorkflowTemplateName,
    ] = useVerification(workflowTemplateName, "workflowTemplateName is required");

    const [substyleOptions, setSubstyleOptions] = useState<IDropdownOption[]>([]);
    const [substyleErr, verifySubStyle] = useVerification(form.substyle, "substyle is required");

    const [languageErr, verifyLanguage] = useVerification(form.language, "language is required");
    const [languageOptions, setLanguageOptions] = useState<IDropdownOption[]>([]);

    const [styleOptions, setStyleOptions] = useState<IDropdownOption[]>([]);
    const [styleErr, verifyStyle] = useVerification(form.style, "style is required");

    const [regionOptions, setRegionOptions] = useState<IDropdownOption[]>([]);
    const [regionError, verifyRegion] = useVerification(form.region, "region is required");

    // const [mjGenerationMode, setMjGenerationModeOptions] = useState<IDropdownOption[]>([]);

    const [imageGenerationModelOptions, setImageGenerationModelOptions] = useState<IDropdownOption[]>([]);
    const [
        imageGenerationModelError, verifyImageGenerationModel,
    ] = useVerification(form.imageGenerationOptions.imageGenerationModel, "model is required");

    const [topicErr, verifyTopic] = useVerification(form.topic, "topic is required");

    const [batchErr, verifyBatch] = useVerification(form.batch, "batch is required");

    const [customOutlineText, setCustomOutlineText] = useState<string | null>(null);

    const history = useNavigate();

    useEffect(() => {
        queryWorkflowTemplates()
            .then((res) => {
                if (res) {
                    setOptions(res.map((item: OnlineWorkflowTemplates) => ({
                        key: item.name,
                        text: item.name,
                    })));
                }
            })
            .catch();
    }, []);

    useEffect(() => {
        querySubstyles({ templateStyle })
            .then((res) => {
                if (res) {
                    setSubstyleOptions(res.map((item: string) => ({
                        key: "substyle",
                        text: item,
                    })));
                }
            })
            .catch();
    }, [templateStyle]);

    useEffect(() => {
        queryStyles({ workflowTemplateName })
            .then((res) => {
                if (res) {
                    setStyleOptions(res.map((item: string) => ({
                        key: "style",
                        text: item,
                    })));
                }
                console.log(res[0]);
                setTemplateStyle(res[0]);
            })
            .catch();
    }, [workflowTemplateName]);

    useEffect(() => {
        queryRegions()
            .then((res) => {
                if (res) {
                    setRegionOptions(res.map((item: string) => ({
                        key: "region",
                        text: item,
                    })));
                }
            })
            .catch();
    }, []);

    useEffect(() => {
        queryLanguages()
            .then((res) => {
                if (res) {
                    setLanguageOptions(res.map((item: string) => ({
                        key: "language",
                        text: item,
                    })));
                }
            })
            .catch();
    }, []);

    // useEffect(() => {
    //     queryMjGenerationModes()
    //         .then((res) => {
    //             if (res) {
    //                 setMjGenerationModeOptions(res.map((item: string) => ({
    //                     key: item,
    //                     text: item,
    //                 })));
    //             }
    //         })
    //         .catch();
    // }, []);

    useEffect(() => {
        queryImageGenerationModels()
            .then((res) => {
                if (res) {
                    setImageGenerationModelOptions(res.map((item: string) => ({
                        key: item,
                        text: item,
                    })));
                }
            })
            .catch();
    }, []);

    const onSubmit = () => {
        setLoading(true);
        if (verifyWorkflowTemplateName() && verifyTopic() && verifyBatch()
            && verifyStyle() && verifySubStyle() && verifyLanguage()) {
            createWorkflow({
                workflowTemplateName,
                data: form,
            }).then((res) => {
                if (res) {
                    history(`/workflow/detail?workflowId=${res}`);
                }
            }).catch((err) => {
                alert(err);
            }).finally(() => {
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    };

    return (
        <div className={style.workflow}>
            <h3 className={style.title}>
                <DefaultButton
                    text="Back"
                    iconProps={{ iconName: "Back" }}
                    onClick={() => history("/workflow")}
                />
                Add Workflow
            </h3>
            <hr />
            <form className={style.form}>
                <Stack horizontal tokens={{ childrenGap: 100 }}>
                    <Stack {...columnProps} style={{ width: "650px" }}>
                        <Dropdown
                            required
                            errorMessage={workflowTemplateNameErr}
                            label="workflowTemplateName"
                            onChange={(e, item) => {
                                setWorkflowTemplateName(item ? String(item.key) : "OnlineModern");
                            }}
                            onBlur={verifyWorkflowTemplateName}
                            options={options}
                        />
                        <TextField
                            required
                            label="batch"
                            errorMessage={batchErr}
                            onChange={(e, value) => {
                                setForm({
                                    ...form,
                                    batch: value || "",
                                });
                            }}
                            onBlur={verifyBatch}
                        />
                        <TextField
                            required
                            label="topic"
                            errorMessage={topicErr}
                            onChange={(e, value) => {
                                setForm({
                                    ...form,
                                    topic: value || "",
                                });
                            }}
                            onBlur={verifyTopic}
                        />
                        <Dropdown
                            required
                            errorMessage={regionError}
                            label="region"
                            onChange={(e, item) => {
                                setTemplateStyle(item ? String(item.text) : "US");
                                setForm({
                                    ...form,
                                    region: item?.text || "",
                                });
                            }}
                            onBlur={verifyRegion}
                            options={regionOptions}
                        />
                        <Dropdown
                            required
                            errorMessage={styleErr}
                            label="style"
                            onChange={(e, item) => {
                                setTemplateStyle(item ? String(item.text) : "Modern");
                                setForm({
                                    ...form,
                                    style: item?.text || "",
                                    substyle: "",
                                });
                            }}
                            onBlur={verifyStyle}
                            options={styleOptions}
                        />
                        <Dropdown
                            required
                            errorMessage={substyleErr}
                            label="substyle"
                            onChange={(e, item) => {
                                setForm({
                                    ...form,
                                    substyle: item?.text || "",
                                });
                            }}
                            onBlur={verifySubStyle}
                            options={substyleOptions}
                        />
                        <Dropdown
                            required
                            errorMessage={languageErr}
                            label="language"
                            onChange={(e, item) => {
                                setForm({
                                    ...form,
                                    language: item?.text || "",
                                });
                            }}
                            onBlur={verifyLanguage}
                            options={languageOptions}
                        />
                    </Stack>
                    <Stack {...columnProps}>
                        <Label>imageManualReviewOptions</Label>
                        <div style={{ marginLeft: 20 }}>
                            <Toggle
                                label="isEnabled"
                                inlineLabel
                                defaultChecked
                                onChange={(e, checked) => {
                                    setForm({
                                        ...form,
                                        imageManualReviewOptions: { isEnabled: !!checked },
                                    });
                                }}
                            />
                        </div>

                        <Label>outlineGenerationOptions</Label>
                        <div style={{ marginLeft: 20 }}>
                            <Toggle
                                label="useMockData"
                                inlineLabel
                                onChange={(e, checked) => {
                                    const newForm = { ...form };
                                    newForm.outlineGenerationOptions.useMockData = !!checked;
                                    setForm(newForm);
                                }}
                            />
                            <Toggle
                                label="rewrite"
                                inlineLabel
                                onChange={(e, checked) => {
                                    const newForm = { ...form };
                                    newForm.outlineGenerationOptions.rewrite = !!checked;
                                    setForm(newForm);
                                }}
                            />
                            <Dropdown
                                label="outlineType"
                                selectedKey={form.outlineGenerationOptions.outlineType}
                                onChange={(e, item) => {
                                    setForm({
                                        ...form,
                                        outlineGenerationOptions: {
                                            ...form.outlineGenerationOptions,
                                            outlineType: item?.text || "RichContent",
                                        },
                                    });
                                }}
                                options={[
                                    { key: "RichContent", text: "RichContent" },
                                    { key: "Frame", text: "Frame" },
                                ]}
                            />
                            <Toggle
                                label="useCustomOutline"
                                inlineLabel
                                onChange={(e, checked) => {
                                    const newForm = { ...form };
                                    newForm.outlineGenerationOptions.useCustomOutline = !!checked;
                                    if (!checked) {
                                        newForm.outlineGenerationOptions.customOutline = null;
                                        setCustomOutlineText(null);
                                    }
                                    setForm(newForm);
                                }}
                            />
                            {form.outlineGenerationOptions.useCustomOutline && (
                                <TextField
                                    label="customOutline"
                                    value={customOutlineText || undefined}
                                    onChange={(e, value) => {
                                        setCustomOutlineText(value || null);
                                    }}
                                    onBlur={() => {
                                        try {
                                            const parsed = JSON.parse(customOutlineText || "{}");
                                            const newForm = { ...form };
                                            newForm.outlineGenerationOptions.customOutline = parsed;
                                            setForm(newForm);
                                        } catch {
                                            // ...handle invalid JSON...
                                        }
                                    }}
                                />
                            )}
                        </div>

                        <Label>imageGenerationOptions</Label>
                        <div style={{ marginLeft: 20 }}>
                            {/* <Toggle
                                label="useMockData"
                                inlineLabel
                                onChange={(e, checked) => {
                                    const newForm = { ...form };
                                    newForm.imageGenerationOptions.useMockData = !!checked;
                                    setForm(newForm);
                                }}
                            /> */}
                            {/* <Dropdown
                                label="generation mode"
                                selectedKey={form.imageGenerationOptions.mjGenerationMode}
                                onChange={(e, item) => {
                                    const newForm = { ...form };
                                    newForm.imageGenerationOptions.mjGenerationMode = item?.text || "RELAX";
                                    setForm(newForm);
                                }}
                                options={mjGenerationMode}
                            /> */}
                            {true && (
                                <Dropdown
                                    label="image generation model"
                                    errorMessage={imageGenerationModelError}
                                    selectedKey={form.imageGenerationOptions.imageGenerationModel}
                                    onChange={(e, item) => {
                                        const newForm = { ...form };
                                        newForm.imageGenerationOptions.imageGenerationModel = item?.text || "RELAX";
                                        setForm(newForm);
                                    }}
                                    onBlur={verifyImageGenerationModel}
                                    options={imageGenerationModelOptions}
                                />
                            )}
                        </div>
                    </Stack>
                </Stack>
            </form>
            <footer className={style.footer}>
                <PrimaryButton onClick={onSubmit} disabled={loading}>
                    提交
                </PrimaryButton>
            </footer>
        </div>
    );
};
